import get from "lodash/get";
import React, { FC, useEffect, useMemo, useState } from "react";
import { DownloadIcon, Select, type StatusOption } from "@pd/pd-ui-kit";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import DropZone from "~/components/DropZone";
import { NOTIFICATIONS } from "~/constans/notifications";
import {
  DealerStatus,
  useAddNotificationMutation,
  useGetMyDealersLazyQuery,
} from "~/schemas/generated/graphql";
import type { UploadDataFormProps } from "~/widgets/UploadDataModal";

import EmployeeDataCheckStep from "./EmployeeDataCheckStep";

import s from "./UploadEmployeeDataForm.less";

type UploadStep = "upload" | "check";

interface IEmployeeUploadForm {
  organization: StatusOption | null;
}

const UploadEmployeeDataForm: FC<UploadDataFormProps> = ({
  formId,
  onClose,
  onStateChange,
}) => {
  const { t } = useTranslation();
  const [addNotificationMutation] = useAddNotificationMutation({});

  const [step, setStep] = useState<UploadStep>("upload");
  const [file, setFile] = useState<File | Record<string, never>>({});

  const { control, watch, handleSubmit } = useForm<IEmployeeUploadForm>({
    defaultValues: {
      organization: null,
    },
  });

  const organizationValue = watch("organization");
  const hasFile = Object.keys(file).length > 0;

  const [getMyDealersQuery, { data: dealersData, loading: dealersLoading }] =
    useGetMyDealersLazyQuery({
      variables: {
        page: 0,
        count: 25,
        partName: null,
        statuses: DealerStatus.Active,
      },
      fetchPolicy: "cache-and-network",
    });

  // форма монтируется только при открытой модалке — запрос как раньше по isOpen
  useEffect(() => {
    getMyDealersQuery();
  }, [getMyDealersQuery]);

  const organizationOptions = useMemo(
    () =>
      (
        get(dealersData, "DealerGetter.getMyDealers.items", []) as {
          id: string;
          DealerName: string;
        }[]
      ).map(({ id, DealerName }) => ({
        label: DealerName,
        value: id,
      })),
    [dealersData],
  );

  useEffect(() => {
    if (step === "check") {
      onStateChange({
        customFooter: true,
        width: 738,
        title: t("employee.uploadCheckTitle"),
      });
    } else {
      onStateChange({
        submitDisabled: !hasFile || !organizationValue?.value,
        width: 492,
        title: t("employee.uploadDataTitle"),
      });
    }
  }, [step, hasFile, organizationValue, onStateChange, t]);

  const handleUploadSubmit = () => {
    // TODO(PASDILER-1952): отправка файла на backend, когда появится API
    setStep("check");
  };

  const handleCheckContinue = () => {
    // TODO(PASDILER-1952): подтверждение загрузки на backend, когда появится API
    addNotificationMutation({
      variables: {
        type: NOTIFICATIONS.success,
        content: t("employee.uploadSuccess"),
      },
    });
    onClose();
  };

  if (step === "check") {
    return (
      <EmployeeDataCheckStep
        onAnotherFile={() => setStep("upload")}
        onClose={onClose}
        onContinue={handleCheckContinue}
      />
    );
  }

  return (
    <form
      id={formId}
      className={s.form}
      onSubmit={handleSubmit(handleUploadSubmit)}
    >
      <a
        className={s.templateCard}
        href="/userfiles/template2.docx"
        download
        target="_blank"
        rel="noreferrer"
      >
        <DownloadIcon size={16} fill="#495a64" />
        <div className={s.templateInfo}>
          <span className={s.templateLabel}>
            {t("employee.downloadEmployeeTemplate")}
          </span>
          <span className={s.templateMeta}>
            {t("employee.employeeTemplateFileMeta")}
          </span>
        </div>
      </a>

      <div className={s.field}>
        <span className={s.label}>{t("employee.organization")}</span>
        <Controller
          name="organization"
          control={control}
          render={({ onChange, value }) => (
            <Select
              labelInValue
              showSearch
              value={value}
              loading={dealersLoading}
              options={organizationOptions}
              placeholder={t("employee.selectOrganization")}
              onChange={(option) => onChange(option || null)}
            />
          )}
        />
      </div>

      <DropZone
        getFiles={(files) => setFile(files[0])}
        setFile={setFile}
        accept=".csv"
        isOne
        customPlaceholder={t("employee.dropCsvFile")}
      />
    </form>
  );
};

export default UploadEmployeeDataForm;
